import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import logo from '../../assets/logo.jpeg';
import {
  FaLinkedin,
  FaInstagram,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    Company: [
      { name: 'About', href: '/about' },
      { name: 'Services', href: '/services' },
      { name: 'Portfolio', href: '/portfolio' },
      { name: 'Careers', href: '/careers' }
    ],
    Resources: [
      { name: 'Blog', href: '/blog' },
      { name: 'Documentation', href: '#' },
      { name: 'API Reference', href: '#' },
      { name: 'Support', href: '#' }
    ]
  };

  const socialLinks = [
    { icon: FaLinkedin, href: 'https://www.linkedin.com/in/qb-kart-it-solutions-90114b410/', label: 'LinkedIn' },
    { icon: FaXTwitter, href: 'https://x.com/QBKartGroup', label: 'Twitter' },
    { icon: FaInstagram, href: 'https://www.instagram.com/qbkartgroup?igsh=MTVkMmF3cmpieTBmbw==', label: 'Instagram' }
  ];

  return (
    <footer className="relative bg-slate-950 border-t border-slate-800/50 text-slate-300">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(34,211,238,0.05),transparent_50%)] pointer-events-none" />

      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="md:col-span-2"
            >
              <Link to="/" className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white border border-slate-700/50 rounded-xl flex items-center justify-center overflow-hidden">
                  <img src={logo} alt="QB Logo" className="h-8 w-auto object-contain" />
                </div>

                <div>
                  <h3 className="font-bold text-white text-xl">
                    QBKart IT Solutions
                  </h3>
                  <p className="text-cyan-400 text-sm">
                    Build Beyond Limits
                  </p>
                </div>
              </Link>

              <p className="text-slate-400 leading-7">
                We deliver enterprise-grade software solutions, AI-powered
                systems, cloud infrastructure, and digital transformation
                services that help organizations scale faster and innovate
                smarter.
              </p>
            </motion.div>

            {/* Footer Links */}
            {Object.entries(footerLinks).map(([category, links], index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1
                }}
                viewport={{ once: true }}
              >
                <h4 className="text-white font-semibold mb-4">
                  {category}
                </h4>

                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.name}>
                      {link.href.startsWith('/') ? (
                        <Link
                          to={link.href}
                          className="text-slate-400 hover:text-cyan-400 transition-colors duration-300"
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-slate-400 hover:text-cyan-400 transition-colors duration-300"
                        >
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.2
              }}
              viewport={{ once: true }}
            >
              <h4 className="text-white font-semibold mb-4">
                Contact
              </h4>

              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex items-start gap-3">
                  <FaMapMarkerAlt className="text-cyan-400 mt-1 shrink-0" />
                  <span>6-1-289/1/1A, Flat-102, Gharonda Apts, Street No-3, Padmaraonagar, Hyderabad, Secunderabad, Telangana, India, 500025</span>
                </li>
                <li className="flex items-center gap-3">
                  <FaPhoneAlt className="text-cyan-400 shrink-0" />
                  <span>+91 91548 75482</span>
                </li>
                <li className="flex items-center gap-3">
                  <FaEnvelope className="text-cyan-400 shrink-0" />
                  <span>pranitha@qbkartitsolutions.com</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent mb-8" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-slate-400 text-sm"
            >
              © {currentYear} QBKart IT Solutions. All rights reserved.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex items-center gap-4"
            >
              {socialLinks.map((social) => {
                const Icon = social.icon;

                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 rounded-full border border-slate-800 hover:border-cyan-500 hover:text-cyan-400 text-slate-400 flex items-center justify-center transition-all duration-300 hover:bg-cyan-500/10"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </motion.div>
          </div>
        </div>

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
      </div>
    </footer>
  );
};

export default Footer;
